import type { LinkedPhone } from '@src/reducers/contactRToolkit';
import { PhoneRequestStatus } from '@src/types/contact';

type CustomFieldSetting = {
  id: number | string;
  required?: boolean;
  activated?: boolean;
};
type ContactAttribute = { attrId: number | string; value: unknown };
type ContactDetail = {
  id: number;
  mobileNumber?: string;
  waPhoneRequestStatus?: PhoneRequestStatus;
};

/**
 * Whether the contact misses a value for a required, active custom field. With no attributes at
 * all, any required field counts as missing.
 */
export const hasMissingRequiredCustomField = (
  customFields: CustomFieldSetting[],
  attributes?: ContactAttribute[]
): boolean => {
  const required = customFields.filter(
    field => field.required && field.activated
  );
  if (!attributes?.length) return required.length > 0;

  return required.some(field => {
    const attribute = attributes.find(item => item.attrId === field.id);
    return !attribute || attribute.value === null;
  });
};

/**
 * The contact detail with a phone linked live through a REQUEST_CONTACT_INFO request
 * (`CONTACT_PHONE_LINKED`, ENG-1304), or `null` when there is nothing to apply: no new link, no
 * detail loaded yet, or a link for another contact. A detail loaded later already comes with the
 * phone from the backend.
 */
export const applyLinkedPhone = <T extends ContactDetail>(
  contactDetail: T | null | undefined,
  linkedPhone: LinkedPhone | null | undefined,
  previousLinkedPhone: LinkedPhone | null | undefined
): T | null => {
  if (!linkedPhone || linkedPhone === previousLinkedPhone) return null;
  if (!contactDetail || contactDetail.id !== linkedPhone.contactId) return null;

  return {
    ...contactDetail,
    mobileNumber: linkedPhone.mobileNumber,
    waPhoneRequestStatus: PhoneRequestStatus.OBTAINED,
  };
};
